
import { supabase } from "@/integrations/supabase/client";
import { Page, TitleConfig, DeviceSpecificSettings } from "../types/page";
import { loadUserSettings, saveUserSetting, parseSettings } from "../utils/userSettingsUtils";
import { fetchDefaultSettings, fetchDefaultPrayerTimes } from "../utils/defaultSettingsUtils";

// Function to load settings from Supabase
export const loadSettingsFromSupabase = async () => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    // Fetch combined settings: user-specific settings take precedence over global settings
    let combinedSettings = [];
    
    // First, fetch global settings (app_settings)
    const { data: globalSettings, error: globalError } = await supabase
      .from('app_settings')
      .select('setting_key, setting_value');
    
    if (globalError) {
      console.error("Error fetching global settings:", globalError);
      throw globalError;
    }
    
    // If user is logged in, also fetch user-specific settings
    if (userId) {
      // Try to load user settings
      const userSettingsData = await loadUserSettings(userId);
      
      // If no user settings found, try to load default settings from the default user
      if (!userSettingsData || userSettingsData.length === 0) {
        console.log("No user settings found, attempting to load default user settings");
        const defaultSettings = await fetchDefaultSettings();
        
        if (defaultSettings && defaultSettings.length > 0) {
          // Map default settings to match the format
          const formattedDefaultSettings = defaultSettings.map(setting => ({
            setting_key: setting.settings_key,
            setting_value: setting.settings_value
          }));
          
          // Load default prayer times
          const defaultPrayerTimes = await fetchDefaultPrayerTimes();
          if (defaultPrayerTimes && defaultPrayerTimes.length > 0) {
            // Add prayer times data
            formattedDefaultSettings.push({
              setting_key: 'prayer_times_data',
              setting_value: defaultPrayerTimes
            });
          }
          
          // Save these default settings to the user if logged in
          if (userId) {
            for (const setting of formattedDefaultSettings) {
              if (setting.setting_key && setting.setting_value) {
                await saveUserSetting(
                  setting.setting_key, 
                  setting.setting_value,
                  userId
                );
              }
            }
          }
          
          // Use the default settings as the user settings
          combinedSettings = formattedDefaultSettings;
          return combinedSettings;
        }
      }
      
      // Map user settings to match global settings format
      const userSettings = userSettingsData?.map(setting => ({
        setting_key: setting.settings_key,
        setting_value: setting.settings_value
      })) || [];
      
      // Create a map of user settings for easy lookup
      const userSettingsMap = new Map();
      userSettings.forEach(setting => {
        userSettingsMap.set(setting.setting_key, setting);
      });
      
      // Merge settings, with user settings taking precedence
      combinedSettings = globalSettings?.map(globalSetting => {
        const userSetting = userSettingsMap.get(globalSetting.setting_key);
        return userSetting || globalSetting;
      }) || [];
      
      // Add any user settings that don't have a global counterpart
      userSettings.forEach(userSetting => {
        if (!combinedSettings.some(s => s.setting_key === userSetting.setting_key)) {
          combinedSettings.push(userSetting);
        }
      });
    } else {
      // If no user, just use global settings
      combinedSettings = globalSettings || [];
    }
    
    return combinedSettings;
  } catch (error) {
    console.error("Error in loadSettingsFromSupabase:", error);
    throw error;
  }
};

// Function to save a setting to the database
export const saveSetting = async (settingKey: string, settingValue: any) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      // Save to user settings - pass the key and value correctly
      const result = await saveUserSetting(settingKey, settingValue, user.id);
      if (!result.success) {
        throw new Error(`Failed to save user setting: ${result.error}`);
      }
    } else {
      // If no user, save to global settings
      const { data: existingSettings, error: checkError } = await supabase
        .from('app_settings')
        .select('id')
        .eq('setting_key', settingKey)
        .limit(1);
      
      if (checkError) throw checkError;
      
      let result;
      
      if (existingSettings && existingSettings.length > 0) {
        // Update existing setting
        result = await supabase
          .from('app_settings')
          .update({ 
            setting_value: settingValue,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings[0].id);
      } else {
        // Insert new setting
        result = await supabase
          .from('app_settings')
          .insert([
            { 
              setting_key: settingKey,
              setting_value: settingValue,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ]);
      }
      
      if (result.error) {
        console.error(`Error saving ${settingKey}:`, result.error);
        throw result.error;
      }
    }
  } catch (error) {
    console.error(`Error saving ${settingKey} to database:`, error);
    throw error;
  }
};

export { parseSettings };
